import './Summary.css';

import { omit } from 'lodash';
import React from 'react';

import Spinner from '../../../../components/Spinner';
import { GetQuoteOk } from '../../../../generated/rating-quoting-graphql';
import { countDecimals } from '../../../../utils/countDecimals';
import {
  formatCADCentsOptional,
  formatCADCentsOptionalWithTwoDecimals
} from '../../../../utils/currencyFormat';
import { lobTypeToProductLabel } from '../Coverages/configs/productLabels';
import SummaryCoverage from './SummaryCoverage';

interface SummaryProps {
  summaryInput: GetQuoteOk;
  /** Titles the section, e.g. "Professional Liability Summary:". */
  lobType: string;
  isEndorsement: boolean;
}

// Coverages that appear on the Coverages card but carry no premium line of their
// own, so a Summary row would only ever read "No Coverage":
//   - workersCompensation is shown as ineligible on the quote page
//   - bipdSublimit (EO) is rated as a factor on the EO premium, not separately
const summaryExcludedCoverages = ['workersCompensation', 'bipdSublimit'];

// Keep the comment out code for future use
const Summary: React.FC<SummaryProps> = (props) => {
  const { summaryInput, isEndorsement, lobType } = props;

  const { quoteNumber, coveragesData, carrierPartner } = summaryInput;

  // BI/PD is a sub-coverage of EO: the rater folds its charge into the EO
  // premium as a factor, so it has no premium of its own and is not listed as a
  // section. Its sublimit still belongs on the EO summary, above the deductible.
  const purchasedBipdSublimit = coveragesData.find(
    (coverageData) =>
      coverageData.name === 'bipdSublimit' && coverageData.chooseCoverage
  )?.limits?.sublimit;

  const withBipdSublimit = (
    name: string,
    limits: Record<string, number>
  ): Record<string, number> =>
    name === 'eo' && purchasedBipdSublimit !== undefined
      ? {
          ...omit(limits, 'deductible'),
          bipdSublimit: purchasedBipdSublimit,
          ...('deductible' in limits ? { deductible: limits.deductible } : {})
        }
      : limits;

  const zeroPremiumText = isEndorsement ? 'No Change' : 'No Coverage';

  return (
    <div className="summary-container lg:max-w-xs text-left border-b-2 border-gray-400 md:border-0">
      <p className="text-xl md:text-2xl ">
        {/* Same label as the Coverages group header, so the two always agree. */}
        <b>{`${lobTypeToProductLabel(lobType)} Summary:`}</b>
      </p>
      <dl className="summary-details overflow-hidden md:overflow-auto max-h-full border-b-2 border-gray-400 border-dashed md:border-solid">
        <div>
          <p className="mt-2 mb-1 text-base md:text-lg">
            Quote Number - {quoteNumber}
          </p>
          {coveragesData
            .filter(
              (coverageData) =>
                !summaryExcludedCoverages.includes(coverageData.name)
            )
            .map(({ name, chooseCoverage, finalPremium, limits }, index) => (
              <SummaryCoverage
                key={index}
                name={name}
                chooseCoverage={chooseCoverage}
                finalPremium={finalPremium}
                limits={withBipdSublimit(name, limits)}
                zeroPremiumText={
                  carrierPartner === 'StateNational' && name === 'tria'
                    ? 'Included'
                    : zeroPremiumText
                }
                carrierPartner={carrierPartner}
              />
            ))}
        </div>
      </dl>
    </div>
  );
};

interface SummaryContainerProps {
  quoteGroups: Array<{
    lobType: string;
    applicationId: string;
    quoteData: GetQuoteOk;
  }>;
  totalPayable: number;
  isEndorsement: boolean;
  /**
   * True while the payment options that `totalPayable` is summed from are
   * being re-read after a re-quote. The total on screen would be the previous
   * quote's, so a spinner takes its place until the refresh lands.
   */
  isPaymentOptionRefreshing?: boolean;
}

export const SummaryContainer: React.FC<SummaryContainerProps> = ({
  quoteGroups,
  totalPayable,
  isEndorsement,
  isPaymentOptionRefreshing = false
}) => {
  const { region } = quoteGroups[0]?.quoteData;

  // Canada's total is the quote's own premium, which is already current when a
  // refresh starts; only a total taken from the payment options can be stale.
  const isTotalFromPaymentOptions = region !== 'Canada';

  const taxMsg = (() => {
    return region === 'Canada' || isEndorsement
      ? 'Taxes and Fees are not included'
      : 'Including Taxes and Fees';
  })();

  // assume yearlyPremium and totalPayable have at most 2 decimal places
  const total = (() => {
    const finalTotal =
      region === 'Canada'
        ? quoteGroups[0].quoteData.premium // assume Canada has only one product
        : totalPayable;
    return countDecimals(finalTotal) > 0
      ? formatCADCentsOptionalWithTwoDecimals.format(finalTotal)
      : formatCADCentsOptional.format(finalTotal);
  })();

  return (
    <div className="summary-list">
      {quoteGroups.map((g) => (
        <Summary
          key={g.quoteData.quoteId}
          summaryInput={g.quoteData}
          lobType={g.lobType}
          isEndorsement={isEndorsement}
        />
      ))}
      <p className="mt-4">
        <b className="text-lg md:text-2xl">Total:</b>
        <span className="float-right px-4 text-lg md:text-lg ">
          {isPaymentOptionRefreshing && isTotalFromPaymentOptions ? (
            <span
              data-testid="summary-total-refreshing"
              role="status"
              aria-label="Updating total"
              className="inline-block align-middle"
            >
              <Spinner as="span" className="inline-block h-6 w-6" />
            </span>
          ) : (
            <b className="font-black text-2xl">{total}</b>
          )}
          /year
        </span>
      </p>
      <p className="inline-block w-full text-xs text-right font-semibold mb-2">
        {taxMsg}
      </p>
    </div>
  );
};

export default Summary;
